/**
 * Application route definitions.
 *
 * Extracted from App.tsx to keep the main entry point focused on providers and layout.
 */
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useMenuSettings } from './contexts/MenuSettingsContext';
import { usePortalMode } from './hooks/usePortalMode';
import MainLayout from './components/layout/MainLayout';
import { PageErrorBoundary } from './components/common';
import { Loader2 } from 'lucide-react';

// Page imports
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ForcePasswordChange from './pages/ForcePasswordChange';
import { CompanyRegistrationForm } from './components/registration/CompanyRegistrationForm';
import Dashboard from './pages/Dashboard';
import Calendar from './pages/Calendar';
import Customers from './pages/Customers';
import Suppliers from './pages/Suppliers';
import CustomerDetail from './pages/CustomerDetail';
import CustomerGroupDetail from './pages/CustomerGroupDetail';
import CustomerPerformance from './pages/CustomerPerformance';
import SitePerformance from './pages/SitePerformance';
import ExchangeRatesPage from './pages/admin/ExchangeRatesPage';
import Settings from './pages/Settings';
import AdminSettings from './pages/AdminSettings';
import AdminSetup from './pages/AdminSetup';
import PlatformAdminPage from './pages/admin/PlatformAdminPage';
import LoginAsPage from './pages/admin/LoginAsPage';
import ImportDashboard from './pages/admin/ImportDashboard';
import EmailQueueDashboard from './pages/admin/EmailQueueDashboard';
import EmailTemplatePreview from './pages/admin/EmailTemplatePreview';
import UsersManagement from './pages/admin/UsersManagement';
import IntegrationsSettings from './pages/admin/IntegrationsSettings';
import PlatformAnalytics from './pages/admin/PlatformAnalytics';
import Purchasing from './pages/Purchasing';
import PartsMaster from './pages/PartsMaster';
import PartSchedulePage from './pages/PartSchedule';
import Quality from './pages/Quality';
import NCREdit from './pages/NCREdit';
import NCRCreate from './pages/NCRCreate';
import Concessions from './pages/Concessions';
import ConcessionDetail from './pages/ConcessionDetail';
import Documents from './pages/Documents';
import InviteUser from './pages/InviteUser';
import UserInvitations from './pages/UserInvitations';
import Users from './pages/Users';
import AcceptInvitation from './pages/AcceptInvitation';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import News from './pages/News';
import NewsDetail from './pages/NewsDetail';
import NewsForm from './pages/NewsForm';
import UserActivity from './pages/UserActivity';
import Notifications from './pages/Notifications';
import { SupplierPortalDashboard, SuppliersList, NewSupplierInvitation, SupplierDetail, SupplierOrders, SupplierDatasets } from './pages/supplier-portal';
import Help from './pages/Help';
import Reports from './pages/Reports';
import PublicDashboard from './pages/PublicDashboard';
import DataImport from './pages/DataImport';
import StockLevelsPage from './pages/StockLevelsPage';
import LogisticsCallOffs from './pages/LogisticsCallOffs';

/**
 * Protected Route wrapper - redirects to login if not authenticated.
 */
export const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Accounts created with an admin-set default password must change it before
  // they can use the portal. Gate every protected page until they do (the
  // /force-password-change route itself requires auth but skips this gate).
  if (user?.password_change_required) {
    return <Navigate to="/force-password-change" replace />;
  }

  return <>{children}</>;
};

/**
 * Smart home redirect. Logistics users get their call-offs page, customers use
 * the platform-configured landing path, everyone else goes to the dashboard.
 */
/**
 * Auth-only wrapper for the forced password change page. Requires a logged-in
 * user but deliberately SKIPS the password_change_required gate (else
 * ProtectedRoute would redirect this route to itself forever).
 */
function ForceChangeRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <ForcePasswordChange />;
}

function HomeRedirect() {
  const { user } = useAuth();
  const { customerDefaultLandingPath, isLoading: settingsLoading } = useMenuSettings();
  const { isCustomer: portalIsCustomer } = usePortalMode();
  if (user?.company_type === 'logistics') {
    return <Navigate to="/logistics/call-offs" replace />;
  }
  const useCustomerLanding =
    user?.company_type === 'customer' || portalIsCustomer;
  if (useCustomerLanding) {
    // Wait for platform settings before routing — otherwise we redirect to
    // /dashboard first and only apply the configured path on next render.
    if (settingsLoading) return null;
    return <Navigate to={customerDefaultLandingPath || '/dashboard'} replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

/**
 * Helper to wrap a page component with ProtectedRoute + MainLayout + PageErrorBoundary.
 */
function protectedPage(pageName: string, element: React.ReactNode) {
  return (
    <ProtectedRoute>
      <MainLayout>
        <PageErrorBoundary pageName={pageName}>
          {element}
        </PageErrorBoundary>
      </MainLayout>
    </ProtectedRoute>
  );
}

/**
 * All application routes.
 */
export function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/force-password-change" element={<ForceChangeRoute />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/register" element={<CompanyRegistrationForm />} />
      <Route path="/accept-invitation" element={<AcceptInvitation />} />
      <Route path="/public/dashboard/:token" element={<PublicDashboard />} />

      {/* Protected routes */}
      <Route path="/dashboard" element={protectedPage("Dashboard", <Dashboard />)} />
      <Route path="/calendar" element={protectedPage("Calendar", <Calendar />)} />
      <Route path="/customers" element={protectedPage("Customers", <Customers />)} />
      <Route path="/suppliers" element={protectedPage("Suppliers", <Suppliers />)} />

      {/* Static and multi-segment customer paths before /customers/:id */}
      <Route path="/customers/groups/:groupId" element={protectedPage("Customer Group Detail", <CustomerGroupDetail />)} />
      <Route path="/customers/performance" element={protectedPage("Customer Performance", <CustomerPerformance />)} />
      <Route path="/site-performance" element={protectedPage("Site Performance", <SitePerformance />)} />
      <Route path="/admin/exchange-rates" element={protectedPage("Exchange Rates", <ExchangeRatesPage />)} />
      <Route path="/customers/:id" element={protectedPage("Customer Detail", <CustomerDetail />)} />

      <Route path="/purchasing" element={protectedPage("Sales Orders", <Purchasing />)} />
      <Route path="/parts-master" element={protectedPage("Parts Master", <PartsMaster />)} />
      <Route path="/part-schedule" element={protectedPage("Part Schedule", <PartSchedulePage />)} />

      {/* Quality */}
      <Route path="/quality" element={protectedPage("Quality", <Quality />)} />
      <Route path="/quality/ncr/new" element={protectedPage("NCR Create", <NCRCreate />)} />
      <Route path="/quality/ncr/:id/edit" element={protectedPage("NCR Edit", <NCREdit />)} />
      <Route path="/concessions" element={protectedPage("Concessions", <Concessions />)} />
      <Route path="/quality/concession/:id" element={protectedPage("Concession Detail", <ConcessionDetail />)} />

      {/* Documents */}
      <Route path="/documents" element={protectedPage("Documents", <Documents />)} />
      <Route path="/user-activity" element={protectedPage("User Activity", <UserActivity />)} />

      {/* News */}
      <Route path="/news" element={protectedPage("News", <News />)} />
      <Route path="/news/create" element={protectedPage("Create News", <NewsForm />)} />
      <Route path="/news/edit/:id" element={protectedPage("Edit News", <NewsForm />)} />
      <Route path="/news/:id" element={protectedPage("News Detail", <NewsDetail />)} />

      {/* Admin */}
      <Route path="/admin" element={protectedPage("Admin Settings", <AdminSettings />)} />
      <Route path="/admin/setup" element={protectedPage("Admin Setup", <AdminSetup />)} />
      <Route path="/admin/platform" element={protectedPage("Platform Admin", <PlatformAdminPage />)} />
      <Route path="/admin/login-as" element={protectedPage("Login As", <LoginAsPage />)} />
      <Route path="/admin/users" element={protectedPage("Users Management", <UsersManagement />)} />
      <Route path="/admin/integrations" element={protectedPage("Integrations Settings", <IntegrationsSettings />)} />
      <Route path="/admin/email-queue" element={protectedPage("Email Queue", <EmailQueueDashboard />)} />
      <Route path="/admin/email-templates" element={protectedPage("Email Templates", <EmailTemplatePreview />)} />
      <Route path="/admin/platform-analytics" element={protectedPage("Platform Analytics", <PlatformAnalytics />)} />
      <Route path="/import-monitor" element={protectedPage("Import Monitor", <ImportDashboard />)} />

      {/* Reports */}
      <Route path="/reports" element={protectedPage("Reports", <Reports />)} />

      {/* Stock Levels */}
      <Route path="/stock-levels" element={protectedPage("Stock Levels", <StockLevelsPage />)} />

      {/* Data Import */}
      <Route path="/data-import" element={protectedPage("Data Import", <DataImport />)} />

      {/* Users */}
      <Route path="/users" element={protectedPage("Users", <Users />)} />
      <Route path="/users/invitations" element={protectedPage("User Invitations", <UserInvitations />)} />
      <Route path="/users/invite" element={protectedPage("Invite User", <InviteUser />)} />

      {/* Settings & Notifications */}
      <Route path="/settings" element={protectedPage("Settings", <Settings />)} />
      <Route path="/notifications" element={protectedPage("Notifications", <Notifications />)} />

      {/* Supplier Portal Mode routes */}
      <Route path="/supplier-portal" element={protectedPage("Customer Portal Dashboard", <SupplierPortalDashboard />)} />
      <Route path="/supplier-portal/suppliers" element={protectedPage("Customers List", <SuppliersList />)} />
      <Route path="/supplier-portal/suppliers/:id" element={protectedPage("Customer Detail", <SupplierDetail />)} />
      <Route path="/supplier-portal/invite" element={protectedPage("Invite Customer", <NewSupplierInvitation />)} />
      <Route path="/supplier-portal/orders" element={protectedPage("Customer Orders", <SupplierOrders />)} />
      <Route path="/supplier-portal/datasets" element={protectedPage("Customer Questionnaires", <SupplierDatasets />)} />

      {/* Logistics */}
      <Route path="/logistics/call-offs" element={protectedPage("Logistics Call-Offs", <LogisticsCallOffs />)} />

      {/* Help */}
      <Route path="/help" element={protectedPage("Help", <Help />)} />

      {/* Redirects */}
      <Route path="/profile" element={<Navigate to="/settings" replace />} />
      <Route path="/" element={<ProtectedRoute><HomeRedirect /></ProtectedRoute>} />
      <Route path="*" element={<ProtectedRoute><HomeRedirect /></ProtectedRoute>} />
    </Routes>
  );
}
